import { supabase } from './supabase.js'

/**
 * Web push, and the iOS rules that shape it:
 *
 *   - It only works when the app was opened from the home-screen icon.
 *     A Safari tab can ask and will silently never deliver.
 *   - The permission prompt has to come from a tap. Asking on load is both
 *     rude and, on iOS, ignored.
 *
 * The public VAPID key is public by design and ships in the bundle. The
 * private half lives only in the Worker's secrets.
 */

const VAPID = import.meta.env.VITE_VAPID_PUBLIC_KEY || ''

export function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  )
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function pushConfigured() {
  return VAPID.length > 0
}

export function permission() {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
}

/** VAPID keys travel base64url; PushManager wants raw bytes. */
function toBytes(base64url) {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4)
  const b64 = (base64url + pad).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

export async function getSubscription() {
  if (!pushSupported()) return null
  const reg = await navigator.serviceWorker.ready
  return reg.pushManager.getSubscription()
}

/**
 * Must be called from a tap. Returns the reason it failed rather than
 * throwing, so the screen can say something true instead of "error".
 */
export async function subscribe() {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' }
  if (!pushConfigured()) return { ok: false, reason: 'unconfigured' }
  if (!isStandalone()) return { ok: false, reason: 'not-installed' }

  const granted = await Notification.requestPermission()
  if (granted !== 'granted') return { ok: false, reason: 'denied' }

  const reg = await navigator.serviceWorker.ready
  const existing = await reg.pushManager.getSubscription()
  const sub = existing || (await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: toBytes(VAPID),
  }))

  const json = sub.toJSON()
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      label: navigator.userAgent.slice(0, 120),
      failed_at: null,
    },
    { onConflict: 'endpoint' },
  )
  if (error) return { ok: false, reason: 'save-failed', message: error.message }

  return { ok: true }
}

/** Unsubscribing removes the row too, or the Worker keeps pushing into a void. */
export async function unsubscribe() {
  const sub = await getSubscription()
  if (!sub) return { ok: true }
  const endpoint = sub.endpoint
  await sub.unsubscribe()
  await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint)
  return { ok: true }
}
